import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { SlideStage } from '../../components/deck/SlideStage';
import { NetworkPathDiagram } from '../../components/deck/NetworkPathDiagram';
import { HandsetState, handsetConditions } from '../../data/deckSlide7';
const VEXU_LOGO = 'https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg';
const ease = [0.22, 0.61, 0.36, 1] as const;
export function AlwaysAvailableSlide() {
  const [pathStage, setPathStage] = useState<0 | 1 | 2>(0);
  const [handsetVisible, setHandsetVisible] = useState(false);
  const [handsetState, setHandsetState] = useState<HandsetState>('available');
  const [emphasise, setEmphasise] = useState(false);
  useEffect(() => {
    setPathStage(0);
    setHandsetVisible(false);
    setHandsetState('available');
    setEmphasise(false);
    const timers: number[] = [];
    const at = (ms: number, fn: () => void) => {
      timers.push(window.setTimeout(fn, ms));
    };

    // Caller → network → Vexu
    at(600, () => setPathStage(1));
    at(1300, () => setPathStage(2));
    // The handset appears beside the path, then becomes unavailable.
    at(2050, () => setHandsetVisible(true));
    at(3100, () => setHandsetState('busy'));
    at(4300, () => setHandsetState('unanswered'));
    at(5500, () => setHandsetState('unreachable'));
    at(6700, () => setHandsetState('off'));
    // The phone is off — Vexu is still on the call.
    at(7400, () => setEmphasise(true));
    return () => timers.forEach(id => window.clearTimeout(id));
  }, []);
  const stateReached = (id: HandsetState) => {
    const order: HandsetState[] = ['available', 'busy', 'unanswered', 'unreachable', 'off'];
    return order.indexOf(handsetState) >= order.indexOf(id);
  };
  return <SlideStage data-id="element-2086" data-mp="10dvhw3ux5r1u">
      <div className="deck-type absolute inset-0 overflow-hidden bg-[#FAFCF9]" data-id="element-2087" data-mp="10eewpbp9vzbn">
        <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 85% 70% at 10% -20%, rgba(0,200,83,0.06) 0%, rgba(250,252,249,0) 62%), linear-gradient(180deg, #FBFDFA 0%, #F5F8F3 58%, #EFF3ED 100%)'
      }} data-id="element-2088" data-mp="wb8ssapmuk8c" />

        {/* Brand header */}
        <motion.div className="absolute flex items-center gap-3" style={{
        left: 112,
        top: 56
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2089" data-mp="ntygblmg75gz">
          <img src={VEXU_LOGO} alt="" className="w-7 h-7 object-contain" data-id="element-2090" data-mp="f0letvabivja" />
          <span className="text-[16px] font-bold tracking-tight text-[#0B1712]" data-id="element-2091" data-mp="juha2pz764ar">
            Vexu.ai
          </span>
        </motion.div>
        <motion.span className="absolute text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8A998F]" style={{
        right: 112,
        top: 62
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.8,
        ease
      }} data-id="element-2092" data-mp="a33g0xq0lwp0">
          07 · Always Available
        </motion.span>

        {/* LEFT — the claim */}
        <motion.h1 className="absolute text-[46px] font-semibold leading-[1.16] tracking-[-0.02em] text-[#0B1712]" style={{
        left: 112,
        top: 196,
        width: 520
      }} animate={{
        opacity: 1,
        scale: handsetState === 'off' ? 1.012 : 1
      }} transition={{
        duration: 0.9,
        ease
      }} data-id="element-2093" data-mp="3do3zexz7r79p">
          {['The AI doesn’t live', 'on your phone.'].map((line, i) => <motion.span key={line} className="block" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.2 + i * 0.16,
          ease
        }} data-id="element-2094" data-mp="3rmaaolt9hyer" data-mpi={i}>
              {line}
            </motion.span>)}
        </motion.h1>

        <motion.p className="absolute text-[18px] leading-relaxed text-[#5A6B60]" style={{
        left: 112,
        top: 332,
        width: 470
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 0.9,
        ease
      }} data-id="element-2095" data-mp="j70h623fu1il">
          Calls are diverted to VEXU at the network level — so the AI can keep
          handling them independently of the handset.
        </motion.p>

        <motion.p className="absolute text-[15px] leading-relaxed text-[#33463B]" style={{
        left: 112,
        top: 424,
        width: 440
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 1.3,
        ease
      }} data-id="element-2096" data-mp="173wpx3g3u09l">
          Your number stays the same. The intelligence lives beyond the device.
        </motion.p>

        {/* Handset conditions */}
        <div className="absolute" style={{
        left: 112,
        top: 522,
        width: 440
      }} data-id="element-2097" data-mp="t9vzcwzb3jkb">
          {handsetConditions.map((condition, i) => {
          const current = handsetState === condition.id;
          return <motion.div key={condition.id} className="flex items-center gap-3 border-t border-[#0B1712]/[0.09] py-[15px]" initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 1.7 + i * 0.14,
            ease
          }} data-id="element-2098" data-mp="24osyx0t49r5x" data-mpi={i}>
                <span className="block rounded-full transition-all duration-500" style={{
              width: 7,
              height: 7,
              backgroundColor: current ? 'rgba(180,84,58,0.8)' : 'rgba(11,23,18,0.16)',
              boxShadow: current ? '0 0 0 5px rgba(180,84,58,0.10)' : 'none'
            }} data-id="element-2099" data-mp="3ffqsy9k7755a" />
                <span className={`text-[17px] transition-colors duration-500 ${current ? 'font-medium text-[#0B1712]' : 'text-[#7C8B82]'}`} data-id="element-2100" data-mp="2rl5xmqgo91t1">
                  {condition.label}
                </span>
              </motion.div>;
        })}
          <div className="border-t border-[#0B1712]/[0.09]" data-id="element-2101" data-mp="2k7oi64caw6e8" />
          <motion.p className="pt-4 text-[15.5px] font-medium text-vexu-700" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.8,
          delay: 2.5,
          ease
        }} data-id="element-2102" data-mp="2oywtx713s3uk">
            VEXU keeps handling the call.
          </motion.p>
        </div>

        {/* IP signal, tied to the differentiated architecture */}
        <motion.div className="absolute flex items-center gap-3" style={{
        left: 112,
        bottom: 62
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.9,
        delay: 2.9,
        ease
      }} data-id="element-2103" data-mp="32x4xvywk45f8">
          <span className="h-px w-6 bg-vexu-600/50" data-id="element-2104" data-mp="2xyju5pi3j3m1" />
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.24em] text-vexu-700/80" data-id="element-2105" data-mp="1ijcng5orbzjz">
            Patent-pending technology
          </span>
        </motion.div>

        {/* RIGHT — the architecture */}
        <motion.div className="absolute" style={{
        left: 660,
        top: 140
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 1,
        delay: 0.4,
        ease
      }} data-id="element-2106" data-mp="1f0duyk5l0bok">
          <NetworkPathDiagram pathStage={pathStage} handsetVisible={handsetVisible} handsetState={handsetState} emphasise={emphasise} data-id="element-2107" data-mp="1oyupfr3qb8fd" />
        </motion.div>

        {/* One restrained comparison line */}
        <motion.div className="absolute flex items-center gap-8 text-[12.5px]" style={{
        left: 660,
        bottom: 62
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.9,
        delay: 2.6,
        ease
      }} data-id="element-2108" data-mp="rc4d1tbho82p">
          <span className="text-[#A3AFA7]" data-id="element-2109" data-mp="117xpzdt4e3l0">
            Device-side AI — depends on the handset
          </span>
          <span className="h-3 w-px bg-[#0B1712]/10" data-id="element-2110" data-mp="8lcvi4hclbd8" />
          <span className="text-[13px] font-semibold text-vexu-700" data-id="element-2111" data-mp="8kl9nldjys05">
            VEXU — operates beyond the handset
          </span>
        </motion.div>
      </div>
    </SlideStage>;
}